/**
 * Align daily index closes to account snapshot dates as % change from range start.
 */

export function getCloseOnOrBefore(closeByDate, targetDate) {
  let bestDate = null;
  let bestClose = null;
  for (const [date, close] of closeByDate) {
    if (date <= targetDate && (!bestDate || date > bestDate)) {
      bestDate = date;
      bestClose = close;
    }
  }
  return bestClose;
}

export function pctFromBaseline(value, baseline) {
  if (!baseline || baseline <= 0 || value == null) return 0;
  return parseFloat((((value / baseline) - 1) * 100).toFixed(2));
}

export function closesMapFromBars(bars) {
  const map = new Map();
  if (!Array.isArray(bars)) return map;
  for (const b of bars) {
    const ts = b?.TimeStamp || b?.TradeTime || b?.Epoch;
    if (!ts) continue;
    const date = String(ts).split('T')[0];
    const close = parseFloat(b.Close ?? b.close);
    if (!date || !Number.isFinite(close)) continue;
    map.set(date, close);
  }
  return map;
}

/**
 * @param {Array<{ time: string, equity?: number, pnlPct?: number, benchmarkPct?: number | null }>} equityCurve
 * @param {Map<string, number>} closeByDate
 * @param {string} rangeStart - YYYY-MM-DD
 */
export function mergeBenchmarkPctIntoCurve(equityCurve, closeByDate, rangeStart) {
  if (!equityCurve?.length || !closeByDate?.size) return equityCurve;

  const baselineClose = getCloseOnOrBefore(closeByDate, rangeStart);
  if (!baselineClose) return equityCurve;

  let lastBenchmarkPct = 0;
  return equityCurve.map((point) => {
    const dayClose = getCloseOnOrBefore(closeByDate, point.time);
    if (dayClose) {
      lastBenchmarkPct = pctFromBaseline(dayClose, baselineClose);
    }
    const benchmarkPct =
      typeof point.benchmarkPct === 'number' ? point.benchmarkPct : lastBenchmarkPct;
    return { ...point, benchmarkPct };
  });
}

export function shiftDateString(dateStr, dayDelta) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + dayDelta);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Attach drawdown ($ below peak equity) to equity curve rows by date. */
export function mergeDrawdownIntoCurve(equityCurve, drawdownCurve) {
  if (!equityCurve?.length || !drawdownCurve?.length) return equityCurve;
  const byTime = new Map(drawdownCurve.map((p) => [p.time, p.drawdown]));
  return equityCurve.map((row) => ({
    ...row,
    drawdown: byTime.has(row.time) ? byTime.get(row.time) : null,
  }));
}

/**
 * Shared percent domain for an account-vs-index chart.
 * A modest move that sits entirely above or below flat includes 0, so a few
 * points of P&L do not stretch to fill the plot. Large trends keep a tight domain.
 */
export function pnlPercentDomain(chartData, includeBenchmark = false) {
  const vals = (chartData || []).flatMap((d) => {
    const row = [d?.pnlPct];
    if (includeBenchmark && typeof d?.benchmarkPct === 'number') row.push(d.benchmarkPct);
    return row.filter((v) => typeof v === 'number' && Number.isFinite(v));
  });
  if (!vals.length) return ['auto', 'auto'];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const pad = Math.max((max - min) * 0.12, 0.4);
  let lo = min - pad;
  let hi = max + pad;
  const span = Math.max(hi - lo, 0.5);
  if (lo > 0 && lo <= span * 1.5) lo = 0;
  if (hi < 0 && Math.abs(hi) <= span * 1.5) hi = 0;
  return [lo, hi];
}

/** Y-axis domain for drawdown (always <= 0). */
export function drawdownYDomain(chartData) {
  const vals = (chartData || [])
    .map((d) => d.drawdown)
    .filter((v) => typeof v === 'number');
  if (!vals.length) return [0, 0];
  const min = Math.min(...vals);
  if (min >= 0) return [0, 0];
  const pad = Math.max(Math.abs(min) * 0.08, 50);
  return [min - pad, 0];
}

export function formatDrawdownAxisTick(value) {
  const v = Number(value);
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 1000) return `-$${(abs / 1000).toFixed(1)}k`;
  if (v === 0) return '$0';
  return `$${v.toFixed(0)}`;
}

/** Starting equity for a snapshot curve (first positive equity in range). */
export function getEquityRangeBaseline(equityCurve) {
  if (!Array.isArray(equityCurve) || !equityCurve.length) return null;
  const baseline = equityCurve.find((p) => Number(p?.equity) > 0)?.equity ?? equityCurve[0]?.equity;
  const n = Number(baseline);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Account P&L from daily snapshots over the selected range.
 * @param {{ equityCurve?: Array, accountPnlPct?: number | null }} equityData
 */
export function getSnapshotPnlMetrics(equityData) {
  const curve = equityData?.equityCurve ?? [];
  const baselineEquity = getEquityRangeBaseline(curve);
  const endEquity = curve.length ? Number(curve[curve.length - 1]?.equity) : null;
  const hasEndEquity = Number.isFinite(endEquity);

  const amount =
    hasEndEquity && baselineEquity != null ? endEquity - baselineEquity : null;

  const pctFromCurve =
    hasEndEquity && baselineEquity != null
      ? pctFromBaseline(endEquity, baselineEquity)
      : null;

  const pct =
    typeof equityData?.accountPnlPct === 'number'
      ? equityData.accountPnlPct
      : pctFromCurve;

  return { amount, pct, baselineEquity, endEquity: hasEndEquity ? endEquity : null };
}

/** Realized trade P&L as % of starting account value in the range. */
export function getRealizedPnlPct(netPnl, baselineEquity) {
  const pnl = Number(netPnl);
  const baseline = Number(baselineEquity);
  if (!Number.isFinite(pnl) || !(baseline > 0)) return null;
  return parseFloat(((pnl / baseline) * 100).toFixed(2));
}
